import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from '../../utils/i18n';
import LanguageSelector from './LanguageSelector';
import { useLanguage } from '../../contexts/LanguageContext';
import { Menu, X, ChevronDown } from 'lucide-react';

interface HeaderProps {
  variant?: 'frontend' | 'auth' | 'admin';
  user?: {
    name: string;
    role: string;
  };
}

const Header: React.FC<HeaderProps> = ({ variant = 'frontend', user }) => {
  const location = useLocation();
  const { currentLanguage, setLanguage } = useLanguage();
  const { t } = useTranslation('header');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);

  const isActive = (path: string) => location.pathname === path;

  if (variant === 'auth') {
    return (
      <header className="auth-header shadow-lg">
        <div className={`flex items-center justify-center gap-4`}>
          <img src="/logo.png" alt="CNCLT logo" className="h-12 w-auto" style={{ filter: 'brightness(0) invert(1)' }} />
        </div>
      </header>
    );
  }

  if (variant === 'admin') {
    return (
      <header className="admin-header shadow-lg">
        <div className="admin-header-content">
          <div className={`flex items-center gap-4`}>
            <img src="/logo.png" alt="CNCLT logo" className="h-10 w-auto" style={{ filter: 'brightness(0) invert(1)' }} />
            <div>
              <h1 className="admin-header-title">{t('adminTitle')}</h1>
              <p className="text-sm text-gray-600">{t('adminSubtitle')}</p>
            </div>
          </div>
          {user && (
            <div className="admin-user-menu">
              <button className="admin-user-button">
                <div className="admin-user-avatar">
                  {user.name.charAt(0).toUpperCase()}
                </div>
                <span>{user.name}</span>
                <span className="text-sm text-charcoal">({user.role})</span>
              </button>
            </div>
          )}
        </div>
      </header>
    );
  }

  // Frontend header
  return (
    <header className="frontend-nav shadow-lg relative z-50">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-5">
        <div className="flex items-center justify-between w-full">
          {/* Section 1: Logo + Navigation Links */}
          <div className="flex items-center gap-4 md:gap-8">
            <Link to="/" className="flex items-center gap-3 flex-shrink-0">
              <img src="/logo.png" alt="CNCLT logo" className="h-9 md:h-12 w-auto" />
            </Link>

            <nav className="hidden xl:flex xl:gap-8 lg:flex lg:gap-6 md:hidden">
              <Link
                to="/"
                className={`frontend-nav-link text-base font-bold ${isActive('/') ? 'text-vibrant-orange' : ''}`}
              >
                {t('home')}
              </Link>
              <div className="relative group">
                <button
                  className={`frontend-nav-link text-base font-bold flex items-center gap-1 ${isActive('/victim-confirmation') || isActive('/signalment') ? 'text-vibrant-orange' : ''}`}
                >
                  {t('victims')}
                  <ChevronDown size={16} />
                </button>
                <div className="absolute top-full left-0 w-48 pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 ease-in-out z-50">
                  <div className="bg-white rounded-lg shadow-xl border border-gray-100 overflow-hidden">
                    <Link
                      to="/victim-confirmation"
                      className={`block px-4 py-3 text-base font-bold hover:bg-gray-50 transition-colors ${isActive('/victim-confirmation') ? 'text-vibrant-orange bg-orange-50' : 'text-dark-blue'}`}
                    >
                      {t('iam_victim')}
                    </Link>
                    <Link
                      to="/signalment"
                      className={`block px-4 py-3 text-base font-bold hover:bg-gray-50 transition-colors ${isActive('/signalment') ? 'text-vibrant-orange bg-orange-50' : 'text-dark-blue'}`}
                    >
                      {t('iam_witness')}
                    </Link>
                    <Link
                      to="/signalment"
                      className={`block px-4 py-3 text-base font-bold hover:bg-gray-50 transition-colors ${isActive('/signalment') ? 'text-vibrant-orange bg-orange-50' : 'text-dark-blue'}`}
                    >
                      {t('iam_reporter')}
                    </Link>
                  </div>
                </div>
              </div>
              <Link
                to="/services"
                className={`frontend-nav-link text-base font-bold ${isActive('/services') ? 'text-vibrant-orange' : ''}`}
              >
                {t('services')}
              </Link>

              <Link
                to="/faq"
                className={`frontend-nav-link text-base font-bold ${isActive('/faq') ? 'text-vibrant-orange' : ''}`}
              >
                {t('faq')}
              </Link>

              <Link
                to="/about"
                className={`frontend-nav-link text-base font-bold ${isActive('/about') ? 'text-vibrant-orange' : ''}`}
              >
                {t('about')}
              </Link>

            </nav>
          </div>

          {/* Section 2: Emblem + Language + Button + Mobile Toggle */}
          <div className="flex items-center gap-3 md:gap-5 lg:gap-6 flex-shrink-0">
            <img src="/logo_armoirie.png" alt="Royaume du Maroc" className="h-8 md:h-11 w-auto hidden sm:block" />

            <div className="flex items-center gap-2">
              <LanguageSelector
                currentLanguage={currentLanguage}
                onLanguageChange={setLanguage}
              />

              {/* Mobile Hamburger Menu Toggle */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="lg:hidden p-2 text-dark-blue hover:bg-gray-100 rounded-md transition-colors"
                aria-label="Toggle menu"
              >
                {isMobileMenuOpen ? <X size={28} className="text-dark-blue" /> : <Menu size={28} />}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 top-[84px] bg-white z-40 overflow-y-auto animate-fade-in">
          <div className="flex flex-col p-6 gap-6">
            <nav className="flex flex-col gap-4">
              <Link
                to="/"
                onClick={() => setIsMobileMenuOpen(false)}
                className={`text-xl font-bold py-3 border-b border-gray-100 ${isActive('/') ? 'text-vibrant-orange' : 'text-dark-blue'}`}
              >
                {t('home')}
              </Link>
              <div className="flex flex-col gap-0 border-b border-gray-100 pb-3">
                <span className={`text-xl font-bold py-2 ${isActive('/victim-confirmation') || isActive('/signalment') ? 'text-vibrant-orange' : 'text-dark-blue'}`}>
                  {t('victims')}
                </span>
                <div className="flex flex-col pl-4 gap-2">
                  <Link
                    to="/victim-confirmation"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`text-xl font-bold py-1 ${isActive('/victim-confirmation') ? 'text-vibrant-orange' : 'text-dark-blue'}`}
                  >
                    {t('iam_victim')}
                  </Link>
                  <Link
                    to="/signalment"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`text-xl font-bold py-1 ${isActive('/signalment') ? 'text-vibrant-orange' : 'text-dark-blue'}`}
                  >
                    {t('iam_witness')}
                  </Link>
                  <Link
                    to="/signalment"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`text-xl font-bold py-1 ${isActive('/signalment') ? 'text-vibrant-orange' : 'text-dark-blue'}`}
                  >
                    {t('iam_reporter')}
                  </Link>
                </div>
              </div>
              <Link
                to="/services"
                onClick={() => setIsMobileMenuOpen(false)}
                className={`text-xl font-bold py-3 border-b border-gray-100 ${isActive('/services') ? 'text-vibrant-orange' : 'text-dark-blue'}`}
              >
                {t('services')}
              </Link>
              <Link
                to="/faq"
                onClick={() => setIsMobileMenuOpen(false)}
                className={`text-xl font-bold py-3 border-b border-gray-100 ${isActive('/faq') ? 'text-vibrant-orange' : 'text-dark-blue'}`}
              >
                {t('faq')}
              </Link>
              <Link
                to="/about"
                onClick={() => setIsMobileMenuOpen(false)}
                className={`text-xl font-bold py-3 border-b border-gray-100 ${isActive('/about') ? 'text-vibrant-orange' : 'text-dark-blue'}`}
              >
                {t('about')}
              </Link>
            </nav>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
